import React, { useState } from "react";
import {
	Badge,
	Box,
	Button,
	Divider,
	Flex,
	FormControl,
	FormLabel,
	HStack,
	ModalBody,
	ModalFooter,
	Progress,
	Select,
	Spinner,
	Stat,
	StatGroup,
	StatHelpText,
	StatLabel,
	StatNumber,
	Table,
	Tbody,
	Td,
	Text,
	Textarea,
	Tfoot,
	Th,
	Thead,
	Tr,
	useToast,
	VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { ClasswizeModal } from "@/components/ui/Modal";
import { colours } from "@/utils/themeUtils";
import { UserInfo } from "js/api/authApi";
import { getApplianceId } from "stores/v2/userSessionStore";
import {
	AiAnalysisResponse,
	AiCategoryCount,
	AiClassStudent,
	AiPromptType,
	AiScreentimeMetrics,
	analyzeStudentActivity,
} from "@/api/aiActivityOverview";
import { Markdown } from "./Markdown";

type Props = {
	students: Partial<UserInfo>[];
	handleClose: () => void;
};

type RangePreset = "last_hour" | "today" | "last_24h";

type AnalysisScope = "student" | "class";

type AnalysisOption = {
	key: string;
	promptType: AiPromptType;
	scope: AnalysisScope;
	label: string;
	/** Only offered when the class has more than one student (e.g. whole-class reports). */
	requiresMultipleStudents?: boolean;
};

// The question dropdown is the single "what to generate" control: scope ("one
// student" vs "whole class") is folded in here rather than a separate checkbox.
const ANALYSIS_OPTIONS: AnalysisOption[] = [
	{ key: "on_task", promptType: "on_task_analysis", scope: "student", label: "Was the student on task?" },
	{ key: "class_summary", promptType: "recent_class_summary", scope: "student", label: "Summarise this class" },
	{ key: "screentime_student", promptType: "screentime_summary", scope: "student", label: "Summarise screen time (one student)" },
	{
		key: "screentime_class",
		promptType: "screentime_summary",
		scope: "class",
		label: "Summarise screen time (whole class)",
		requiresMultipleStudents: true,
	},
];

const RANGE_OPTIONS: { value: RangePreset; label: string }[] = [
	{ value: "last_hour", label: "Last hour" },
	{ value: "today", label: "Today" },
	{ value: "last_24h", label: "Last 24 hours" },
];

function displayName(student: Partial<UserInfo>): string {
	const full = `${student.firstName ?? ""} ${student.lastName ?? ""}`.trim();
	return full || (student.username ?? "Unknown student");
}

function computeRange(preset: RangePreset): { start: string; end: string } {
	const end = moment();
	let start = moment().startOf("day"); // "today"
	if (preset === "last_hour") {
		start = moment().subtract(1, "hour");
	} else if (preset === "last_24h") {
		start = moment().subtract(24, "hours");
	}
	return { start: start.toISOString(), end: end.toISOString() };
}

function AIActivityOverviewModal({ students, handleClose }: Props): JSX.Element {
	const toast = useToast();

	const [selectedUsername, setSelectedUsername] = useState<string>(students[0]?.username ?? "");
	const [selectedKey, setSelectedKey] = useState<string>("on_task");
	const [range, setRange] = useState<RangePreset>("today");
	const [topic, setTopic] = useState<string>("");

	const availableOptions = ANALYSIS_OPTIONS.filter((o) => !o.requiresMultipleStudents || students.length > 1);
	const selectedOption = availableOptions.find((o) => o.key === selectedKey) ?? availableOptions[0];
	const promptType = selectedOption.promptType;
	const classMode = selectedOption.scope === "class";
	// Lesson topic only informs the on-task judgement; it's irrelevant to the screen-time report.
	const showTopic = promptType !== "screentime_summary";

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [data, setData] = useState<AiAnalysisResponse | null>(null);

	const handleAnalyze = async (): Promise<void> => {
		if (!classMode && !selectedUsername) {
			setError("Select a student first.");
			return;
		}
		setLoading(true);
		setError(null);
		setData(null);
		try {
			const { start, end } = computeRange(range);
			const classUsernames = students.map((s) => s.username).filter((u): u is string => !!u);
			const result = await analyzeStudentActivity({
				applianceId: getApplianceId(),
				studentUsername: selectedUsername || classUsernames[0] || "",
				start,
				end,
				promptType,
				topic: showTopic ? topic.trim() || undefined : undefined,
				students: classMode ? classUsernames : undefined,
			});
			setData(result);
		} catch (e) {
			const message = e instanceof Error ? e.message : "Something went wrong.";
			setError(message);
			toast({ title: "Could not generate overview", description: message, status: "error", isClosable: true });
		} finally {
			setLoading(false);
		}
	};

	return (
		<ClasswizeModal
			isOpen={true}
			onClose={handleClose}
			title="AI Activity Overview"
			dataTestId="ai-overview-modal"
			size="2xl"
			scrollBehavior="inside"
		>
			<ModalBody>
				<VStack align="stretch" spacing={4}>
					<Text fontSize="sm" color={colours.blueGray[400]}>
						Generate an AI summary of a student&apos;s recent activity, grounded in their screenshots and browsing timeline.
					</Text>

					<Flex gap={3} wrap="wrap">
						{students.length > 1 && !classMode && (
							<FormControl flex="1 1 220px">
								<FormLabel fontSize="sm">Student</FormLabel>
								<Select value={selectedUsername} onChange={(e) => setSelectedUsername(e.target.value)} data-testid="ai-overview-student">
									{students.map((s, i) => (
										<option key={s.username ?? String(i)} value={s.username ?? ""}>
											{displayName(s)}
										</option>
									))}
								</Select>
							</FormControl>
						)}

						<FormControl flex="1 1 220px">
							<FormLabel fontSize="sm">Question</FormLabel>
							<Select value={selectedOption.key} onChange={(e) => setSelectedKey(e.target.value)} data-testid="ai-overview-prompt">
								{availableOptions.map((o) => (
									<option key={o.key} value={o.key}>
										{o.label}
									</option>
								))}
							</Select>
						</FormControl>

						<FormControl flex="0 1 160px">
							<FormLabel fontSize="sm">Time range</FormLabel>
							<Select value={range} onChange={(e) => setRange(e.target.value as RangePreset)} data-testid="ai-overview-range">
								{RANGE_OPTIONS.map((o) => (
									<option key={o.value} value={o.value}>
										{o.label}
									</option>
								))}
							</Select>
						</FormControl>
					</Flex>

					{showTopic && (
						<FormControl>
							<FormLabel fontSize="sm">
								Lesson topic{" "}
								<Text as="span" fontWeight={400} color={colours.blueGray[400]}>
									(optional — helps decide what counts as on-task)
								</Text>
							</FormLabel>
							<Textarea
								value={topic}
								onChange={(e) => setTopic(e.target.value)}
								placeholder="e.g. Year 9 algebra — solving linear equations in Mathspace"
								rows={2}
								resize="vertical"
								data-testid="ai-overview-topic"
							/>
						</FormControl>
					)}

					<Box>
						<Button
							colorScheme="blue"
							onClick={() => void handleAnalyze()}
							isLoading={loading}
							loadingText="Analysing…"
							isDisabled={!classMode && !selectedUsername}
							data-testid="ai-overview-analyse"
						>
							Generate overview
						</Button>
					</Box>

					<Divider />

					{loading && (
						<VStack py={8} spacing={3}>
							<Spinner />
							<Text fontSize="sm" color={colours.blueGray[400]}>
								Reviewing screenshots and activity — this can take up to 30 seconds.
							</Text>
						</VStack>
					)}

					{!loading && error && (
						<Text fontSize="sm" color={colours.red[500]} data-testid="ai-overview-error">
							{error}
						</Text>
					)}

					{!loading && !error && !data && (
						<Text fontSize="sm" color={colours.blueGray[400]}>
							Choose a question and time range, then generate an overview.
						</Text>
					)}

					{!loading && data && <AnalysisResult data={data} />}
				</VStack>
			</ModalBody>

			<ModalFooter>
				<Button variant="ghost" onClick={handleClose}>
					Close
				</Button>
			</ModalFooter>
		</ClasswizeModal>
	);
}

function AnalysisResult({ data }: { data: AiAnalysisResponse }): JSX.Element {
	const st = data.screentime;
	const classRows = data.classStudents;
	const pct = Math.max(0, Math.min(100, data.chartData.onTaskPercentage));
	// On-task bar is meaningful for the timeline report, and for screentime when
	// there's any classified on/off time.
	const showOnTask = data.timeline.length > 0 || (!!st && st.onTaskMinutes + st.offTaskMinutes > 0);
	const commentaryTitle = st || classRows ? "Commentary" : "AI summary";

	return (
		<VStack align="stretch" spacing={4} data-testid="ai-overview-result">
			{/* Screentime headline metrics */}
			{st && <ScreentimeHeader m={st} isClass={!!classRows} />}

			{/* On-task summary */}
			{showOnTask && (
				<Box>
					<HStack justify="space-between" mb={1}>
						<Text fontWeight={600} fontSize="sm">
							On-task time
						</Text>
						<Text fontWeight={700} fontSize="sm">
							{pct}%
						</Text>
					</HStack>
					<Progress value={pct} colorScheme={pct >= 50 ? "green" : "orange"} borderRadius="full" size="sm" />
				</Box>
			)}

			{/* Time-per-category table */}
			{data.chartData.categoryBreakdown.length > 0 && <CategoryTable rows={data.chartData.categoryBreakdown} />}

			{/* Per-student breakdown (whole-class report) */}
			{classRows && classRows.length > 0 && <ClassStudentsTable rows={classRows} />}

			{/* Timeline */}
			{data.timeline.length > 0 && (
				<Box>
					<Text fontWeight={600} fontSize="sm" mb={2}>
						Timeline
					</Text>
					<VStack align="stretch" spacing={2}>
						{data.timeline.map((entry, i) => (
							<HStack key={i} align="start" spacing={3} borderBottomWidth="1px" borderColor={colours.neutrals[50]} pb={2}>
								<Text fontSize="xs" color={colours.blueGray[400]} minW="92px" whiteSpace="nowrap">
									{moment(entry.start).format("h:mm a")} – {moment(entry.end).format("h:mm a")}
								</Text>
								<Box flex="1">
									<Text fontSize="sm" fontWeight={500}>
										{entry.activity}
									</Text>
									<Text fontSize="xs" color={colours.blueGray[400]}>
										{entry.category}
										{entry.screenshotConfirmed ? " · ✓ seen in screenshot" : " · metadata only"}
									</Text>
								</Box>
								<VStack align="end" spacing={1}>
									<Badge colorScheme={entry.onTask ? "green" : "red"} variant="subtle">
										{entry.onTask ? "On task" : "Off task"}
									</Badge>
									{entry.blocked && (
										<Badge colorScheme="orange" variant="solid">
											Blocked
										</Badge>
									)}
								</VStack>
							</HStack>
						))}
					</VStack>
				</Box>
			)}

			{/* AI commentary / summary */}
			<Box>
				<Text fontWeight={600} fontSize="sm" mb={2}>
					{commentaryTitle}
				</Text>
				<Box bg={colours.gray[50]} borderRadius="md" p={3}>
					<Markdown content={data.summary} />
				</Box>
			</Box>

			{/* Metadata footer */}
			<Text fontSize="xs" color={colours.blueGray[300]}>
				{data.metadata.model} · {data.metadata.screenshotsAnalyzed} {st ? "activity records" : "screenshots analysed"} ·{" "}
				{(data.metadata.processingTimeMs / 1000).toFixed(1)}s
			</Text>
		</VStack>
	);
}

function ScreentimeHeader({ m, isClass }: { m: AiScreentimeMetrics; isClass: boolean }): JSX.Element {
	return (
		<Box>
			<StatGroup gap={6} flexWrap="wrap">
				<Stat flex="0 0 auto">
					<StatLabel fontSize="xs">Total screen time</StatLabel>
					<StatNumber fontSize="lg">{formatMinutes(m.totalMinutes)}</StatNumber>
				</Stat>
				<Stat flex="0 0 auto">
					<StatLabel fontSize="xs">{isClass ? "Tab switches (class)" : "Tab switches"}</StatLabel>
					<StatNumber fontSize="lg">{m.switches}</StatNumber>
				</Stat>
				<Stat flex="0 0 auto">
					<StatLabel fontSize="xs">Longest stretch</StatLabel>
					<StatNumber fontSize="lg">{formatMinutes(m.longestMinutes)}</StatNumber>
					{m.longestActivity !== "" && (
						<StatHelpText mb={0} noOfLines={1}>
							{m.longestActivity}
						</StatHelpText>
					)}
				</Stat>
				{m.blockedAttempts > 0 && (
					<Stat flex="0 0 auto">
						<StatLabel fontSize="xs">Blocked attempts</StatLabel>
						<StatNumber fontSize="lg" color={colours.red[500]}>
							{m.blockedAttempts}
						</StatNumber>
					</Stat>
				)}
			</StatGroup>
			{m.totalMinutes > 0 && (
				<Text fontSize="xs" color={colours.blueGray[400]} mt={1}>
					Active {moment(m.activeFrom).format("h:mm a")} – {moment(m.activeTo).format("h:mm a")}
				</Text>
			)}
		</Box>
	);
}

function ClassStudentsTable({ rows }: { rows: AiClassStudent[] }): JSX.Element {
	return (
		<Box>
			<Text fontWeight={600} fontSize="sm" mb={2}>
				Per-student breakdown
			</Text>
			<Table size="sm" variant="simple">
				<Thead>
					<Tr>
						<Th>Student</Th>
						<Th isNumeric>Total</Th>
						<Th isNumeric>On-task</Th>
						<Th>Top type</Th>
					</Tr>
				</Thead>
				<Tbody>
					{rows.map((r) => {
						const denom = r.onTaskMinutes + r.offTaskMinutes;
						const onPct = denom > 0 ? Math.round((r.onTaskMinutes / denom) * 100) : 0;
						return (
							<Tr key={r.student}>
								<Td maxW="220px" isTruncated title={r.student}>
									{r.student}
								</Td>
								{r.error ? (
									<Td colSpan={3} color={colours.blueGray[300]}>
										no data
									</Td>
								) : (
									<>
										<Td isNumeric>{formatMinutes(r.totalMinutes)}</Td>
										<Td isNumeric>{onPct}%</Td>
										<Td>{r.topCategory || "—"}</Td>
									</>
								)}
							</Tr>
						);
					})}
				</Tbody>
			</Table>
		</Box>
	);
}

function formatMinutes(min: number): string {
	if (min < 60) return `${min} min`;
	const h = Math.floor(min / 60);
	const m = min % 60;
	return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function CategoryTable({ rows }: { rows: AiCategoryCount[] }): JSX.Element {
	const total = rows.reduce((sum, r) => sum + r.minutes, 0);
	return (
		<Box>
			<Text fontWeight={600} fontSize="sm" mb={2}>
				Time by task type
			</Text>
			<Table size="sm" variant="simple">
				<Thead>
					<Tr>
						<Th>Task type</Th>
						<Th isNumeric>Time</Th>
						<Th isNumeric>Share</Th>
					</Tr>
				</Thead>
				<Tbody>
					{rows.map((r) => (
						<Tr key={r.category}>
							<Td>{r.category}</Td>
							<Td isNumeric>{formatMinutes(r.minutes)}</Td>
							<Td isNumeric>{total > 0 ? Math.round((r.minutes / total) * 100) : 0}%</Td>
						</Tr>
					))}
				</Tbody>
				<Tfoot>
					<Tr>
						<Th>Total</Th>
						<Th isNumeric>{formatMinutes(total)}</Th>
						<Th isNumeric>100%</Th>
					</Tr>
				</Tfoot>
			</Table>
		</Box>
	);
}

export default AIActivityOverviewModal;
